import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Sparkles, Star, Trash2, Heart, Briefcase, Activity, Clock, DollarSign, Palette } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const THEME_ICONS = {
  love: Heart,
  career: Briefcase,
  health: Activity,
  longevity: Clock,
  finances: DollarSign,
  creativity: Palette,
  spirituality: Star,
};

const THEME_NAMES = {
  love: 'Amour',
  career: 'Carrière',
  health: 'Santé',
  longevity: 'Longévité',
  finances: 'Finances',
  creativity: 'Créativité',
  spirituality: 'Spiritualité',
};

const FavoritesPage = ({ sessionId }) => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFavorites = async () => {
      if (!sessionId) return;

      try {
        const response = await axios.get(`${API}/favorites/${sessionId}`);
        setFavorites(response.data);
      } catch (error) {
        console.error('Error fetching favorites:', error);
        toast.error('Erreur lors du chargement des favoris');
      } finally {
        setIsLoading(false);
      }
    };

    fetchFavorites();
  }, [sessionId]);

  const removeFavorite = async (favoriteId) => {
    try {
      await axios.delete(`${API}/favorites/${favoriteId}?session_id=${sessionId}`);
      setFavorites(favorites.filter(f => f.id !== favoriteId));
      toast.success('Retiré des favoris');
    } catch (error) {
      console.error('Error removing favorite:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-secondary/20 py-12 px-4 md:px-8">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <Button
            data-testid="back-button"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-6 rounded-full hover:bg-secondary/50"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'accueil
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
              <Star className="h-6 w-6 text-yellow-600 fill-yellow-500" />
            </div>
            <div>
              <h1 className="font-heading text-3xl md:text-4xl">
                Mes favoris
              </h1>
              <p className="text-muted-foreground">
                Vos insights préférés sauvegardés
              </p>
            </div>
          </div>
        </motion.div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Sparkles className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : favorites.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-yellow-50 flex items-center justify-center">
              <Star className="h-10 w-10 text-yellow-400" />
            </div>
            <h2 className="font-heading text-2xl mb-4">Aucun favori pour le moment</h2>
            <p className="text-muted-foreground mb-8 max-w-md mx-auto">
              Ajoutez des insights à vos favoris depuis vos lectures pour les retrouver facilement ici
            </p>
            <Button
              onClick={() => navigate('/history')}
              className="rounded-full bg-primary hover:bg-primary/90 px-8 py-6"
            >
              Voir mes lectures
            </Button>
          </motion.div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2">
            <AnimatePresence>
              {favorites.map((favorite, index) => {
                const Icon = THEME_ICONS[favorite.theme] || Sparkles;
                
                return (
                  <motion.div
                    key={favorite.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ delay: index * 0.05 }}
                    className="bg-card p-6 rounded-2xl border border-border shadow-sm hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-secondary/50 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-heading text-lg">{favorite.title}</h3>
                          <span className="text-xs text-muted-foreground">
                            {THEME_NAMES[favorite.theme] || favorite.theme} • {formatDate(favorite.created_at)}
                          </span>
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFavorite(favorite.id)}
                        className="text-muted-foreground hover:text-destructive rounded-full"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {favorite.interpretation}
                      </p>
                      
                      {favorite.advice && (
                        <div className="bg-secondary/30 p-3 rounded-xl">
                          <p className="text-xs font-medium mb-1">Conseil</p>
                          <p className="text-xs text-muted-foreground">
                            {favorite.advice}
                          </p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default FavoritesPage;
